import { PropertyFeatures } from '../../../../../app-kit/components/PropertyFeatures/PropertyFeatures';
import type { FunctionComponent } from 'react';
import { Fragment, useState } from 'react';
import { WebLink } from '../../../../../app-kit/components/WebLink/WebLink';
import type { PropertyAccordionItem } from '../../types';
import {
  DesktopAccordionFactsSubtitle,
  DesktopAccordionFactsSubtitleWrapper,
  DesktopSoldDate,
  FactsSubtitle,
  FactsTitle,
  FactWrapper,
  LinksWrapper,
  MobileAccordionFactsTable,
  PropertyAddressTitle,
  PropertyFeaturesWrapper,
  StyledAccordionItem,
  StyledDivider,
  StyledImage,
  StyledPicture,
  TitleWrapper,
} from './ComparablePropertyAccordionItem.styled';
import { trackAccordionInteraction } from '../../../../../analytics/event-tracking/trackAccordionInteraction';
import { TABLET_MAX, BadgeWithStatus } from '@property.com.au/ui-kit';
import { mapBadgeVariant } from '../../../../../app-kit/helpers/mapBadgeVariant';
import { mapLinkIconPlacement } from '../../../../../app-kit/helpers/mapLinkIconPlacement';
import { mapApiWeblinkVariantToLinkVariant } from '../../../../../app-kit/helpers/mapApiWeblinkVariantToLinkVariant';

export const ComparablePropertyAccordionItem: FunctionComponent<PropertyAccordionItem> = ({
  isDefaultOpen,
  tracking,
  property: { address, features, soldDate, propertyFacts, image, weblinks, badge },
}) => {
  const [expanded, setExpanded] = useState(isDefaultOpen);

  return (
    <StyledAccordionItem
      id={address}
      key={address}
      title={
        <TitleWrapper>
          <PropertyAddressTitle>{address}</PropertyAddressTitle>
          <DesktopAccordionFactsSubtitleWrapper>
            {propertyFacts.map((fact, index) => (
              <DesktopAccordionFactsSubtitle
                key={fact.title + fact.subtitle}
                index={index}
                title={fact.title}
              >
                {fact.subtitle}
              </DesktopAccordionFactsSubtitle>
            ))}
          </DesktopAccordionFactsSubtitleWrapper>
        </TitleWrapper>
      }
      headingLevel={0} // Removes automatic wrapping of title prop as semantic heading
      defaultExpanded={isDefaultOpen}
      onToggle={(_expanded) => {
        setExpanded(_expanded ?? false);
        trackAccordionInteraction(_expanded ? tracking.expand : tracking.collapse);
      }}
      dangerouslySetClassNames={{
        header: 'header',
        headerButton: 'headerButton',
        contentWrapper: 'contentWrapper',
        content: 'content',
        textTitle: 'textTitle',
        chevronWrapper: 'chevronWrapper',
      }}
    >
      <PropertyFeaturesWrapper>
        {features && (
          <PropertyFeatures
            items={features.map((feature) => ({ ...feature, iconName: feature.iconNameV2 }))}
          />
        )}
        <DesktopSoldDate>
          <BadgeWithStatus variant={mapBadgeVariant(badge.variant)}>{badge.text}</BadgeWithStatus>
          <FactsSubtitle>{soldDate}</FactsSubtitle>
        </DesktopSoldDate>
      </PropertyFeaturesWrapper>

      <MobileAccordionFactsTable>
        <FactWrapper>
          <div>
            <BadgeWithStatus variant={mapBadgeVariant(badge.variant)}>{badge.text}</BadgeWithStatus>
          </div>
          <FactsSubtitle>{soldDate}</FactsSubtitle>
        </FactWrapper>
        {propertyFacts.map(({ title, subtitle }) => (
          <Fragment key={title}>
            <StyledDivider />
            <FactWrapper>
              <FactsTitle>{title}</FactsTitle>
              <FactsSubtitle title={title}>{subtitle}</FactsSubtitle>
            </FactWrapper>
          </Fragment>
        ))}
      </MobileAccordionFactsTable>

      {image && (
        <StyledPicture>
          <source
            srcSet={image.desktopWebpUri}
            media={`(min-width: ${TABLET_MAX}px)`}
            type="image/webp"
          />
          <source srcSet={image.mobileWebpUri} type="image/webp" />
          <source
            srcSet={image.desktopJpgUri}
            media={`(min-width: ${TABLET_MAX}px)`}
            type="image/jpeg"
          />
          <StyledImage alt={image.alt} src={image.mobileJpgUri} loading="lazy" />
        </StyledPicture>
      )}

      {weblinks && (
        <LinksWrapper>
          {weblinks.map((link) => (
            <WebLink
              {...link}
              key={link.key}
              iconInfo={
                link.iconInfo
                  ? {
                      ...link.iconInfo,
                      placement: mapLinkIconPlacement(link.iconInfo.placement),
                    }
                  : null
              }
              variant={mapApiWeblinkVariantToLinkVariant(link.variant)}
              tracking={link.tracking}
              sizeVariant="medium"
              {...(expanded ? {} : { tabIndex: -1 })}
            />
          ))}
        </LinksWrapper>
      )}
    </StyledAccordionItem>
  );
};
